import { useEffect, useState, useCallback } from 'react';
import { adminGet, adminPost } from './adminApi';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const mono = { fontFamily: "'IBM Plex Mono', monospace" };

export default function AdminQCPage() {
  const [queue, setQueue] = useState([]);
  const [selected, setSelected] = useState(null);
  const [types, setTypes] = useState([]);
  const [firmware, setFirmware] = useState([]);
  const [checks, setChecks] = useState({});
  const [targetFw, setTargetFw] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminGet('/qc/queue').then(list => {
      setQueue(list);
      setSelected(prev => list.find(d => d.id === prev?.id) || list[0] || null);
    }).catch(e => toast.error(e.message));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { adminGet('/device-types').then(setTypes).catch(() => {}); }, []);
  const selectedId = selected?.id;
  const selectedType = selected?.device_type;
  useEffect(() => {
    if (!selectedId) return;
    setChecks({});
    adminGet(`/firmware?device_type=${selectedType}`).then(fw => {
      setFirmware(fw);
      const approved = fw.filter(f => f.approved);
      setTargetFw(approved[0]?.version || '');
    }).catch(() => {});
  }, [selectedId, selectedType]);

  const dtype = types.find(t => t.code === selected?.device_type);
  const qcItems = dtype?.qc_items || [];

  const setFw = async () => {
    if (!targetFw) return;
    setBusy(true);
    try {
      await adminPost(`/devices/${selected.id}/set-firmware`, { version: targetFw });
      toast.success(`Firmware ${targetFw} set`);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const submit = async (result) => {
    let reason = null;
    if (result === 'fail') {
      reason = window.prompt('RMA reason (required):');
      if (!reason) return;
    }
    setBusy(true);
    try {
      const res = await adminPost(`/qc/${selected.id}/submit`, {
        results: checks, result, failure_reason: reason,
      });
      if (result === 'pass') {
        toast.success(res.moved_to_stock ? 'QC passed · moved to stock' : 'QC passed · set firmware to complete assembly');
      } else {
        toast.success('Marked failed · sent to RMA');
      }
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen" data-testid="admin-qc-page">
      <div className="w-96 shrink-0 border-r p-6 bg-white" style={{ borderColor: '#E5E3DC' }}>
        <h1 className="text-2xl font-bold mb-4" style={{ color: '#1B1D1C' }}>QC queue</h1>
        <div className="space-y-2">
          {queue.map(d => (
            <button key={d.id} data-testid={`qc-queue-item-${d.id}`} onClick={() => setSelected(d)}
              className={cn('w-full text-left rounded-lg border p-3.5 transition-colors')}
              style={selected?.id === d.id ? { background: '#E7F2EE', borderColor: '#BFDCD2' } : { borderColor: '#E5E3DC' }}>
              <p className="font-bold" style={mono}>{d.trackotic_id || d.imei}</p>
              <p className="text-xs mt-0.5" style={{ color: '#6b7069' }}>
                {d.device_type_display} · SIM bound{d.stage === 'rma' ? ' · RMA' : selected?.id === d.id ? ' · testing' : ''}
              </p>
            </button>
          ))}
          {queue.length === 0 && <p className="text-sm" style={{ color: '#9a9e96' }}>Queue empty — nothing awaiting QC</p>}
        </div>
      </div>

      {selected ? (
        <div className="flex-1 p-8 flex gap-6 min-w-0">
          <div className="flex-1 min-w-0">
            <h1 className="text-4xl font-bold" style={{ ...mono, color: '#1B1D1C' }} data-testid="qc-device-id">{selected.trackotic_id || selected.imei}</h1>
            <p className="text-sm mt-1 mb-5" style={{ color: '#6b7069' }}>
              {selected.device_type_display} · IMEI <span style={mono}>{selected.imei}</span> · MSISDN <span style={mono}>{selected.msisdn || '—'}</span>
            </p>
            <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="qc-checklist">
              {qcItems.map(item => (
                <label key={item.key} className="flex items-center justify-between py-3 border-b cursor-pointer" style={{ borderColor: '#F3F1EA' }}>
                  <span className="flex items-center gap-3 text-sm" style={{ color: '#1B1D1C' }}>
                    <input type="checkbox" data-testid={`qc-check-${item.key}`} checked={!!checks[item.key]}
                      onChange={e => setChecks({ ...checks, [item.key]: e.target.checked })}
                      className="w-4 h-4 accent-[#0B6B55]" />
                    {item.label}
                  </span>
                  <span className="text-xs" style={{ color: '#9a9e96' }}>{checks[item.key] ? 'Pass' : (item.detail || '')}</span>
                </label>
              ))}
            </div>
            <p className="text-sm mt-3" style={{ color: '#9a9e96' }}>
              Checklist items shown depend on device type — sensors a type does not have are hidden.
            </p>
          </div>

          <div className="w-80 shrink-0 space-y-4">
            <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="firmware-card">
              <h2 className="text-lg font-bold" style={{ color: '#1B1D1C' }}>Firmware</h2>
              <p className="text-sm mt-2" style={{ color: '#6b7069' }}>Installed</p>
              <p className="text-lg font-bold" style={mono}>{selected.firmware || '—'}</p>
              <p className="text-sm mt-3 mb-1" style={{ color: '#6b7069' }}>Target version for {selected.device_type_display}</p>
              <select data-testid="firmware-select" value={targetFw} onChange={e => setTargetFw(e.target.value)}
                className="w-full h-9 px-3 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
                {firmware.filter(f => f.approved).map(f => <option key={f.version} value={f.version}>{f.version} (approved)</option>)}
              </select>
              <Button variant="outline" disabled={busy || !targetFw} data-testid="flash-ota-btn" onClick={setFw}
                className="w-full mt-3 bg-white" style={{ borderColor: '#0B6B55', color: '#0B6B55' }}>Flash / push OTA</Button>
              <p className="text-xs mt-2" style={{ color: '#9a9e96' }}>Only versions approved for this device type appear here.</p>
            </div>
            <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="qc-result-card">
              <h2 className="text-lg font-bold mb-3" style={{ color: '#1B1D1C' }}>Result</h2>
              <Button disabled={busy} data-testid="qc-pass-btn" onClick={() => submit('pass')}
                className="w-full text-white hover:opacity-90 mb-3" style={{ background: '#0B6B55' }}>Pass → move to stock</Button>
              <Button variant="outline" disabled={busy} data-testid="qc-fail-btn" onClick={() => submit('fail')}
                className="w-full bg-white" style={{ borderColor: '#B54545', color: '#B54545' }}>Fail → RMA with reason</Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 p-8" style={{ color: '#9a9e96' }} data-testid="qc-empty">Select a device from the queue</div>
      )}
    </div>
  );
}
